import { useEffect, useState, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button from '../../components/ui/Button.jsx'
import Tabs from '../../components/ui/Tabs.jsx'
import { Select } from '../../components/ui/Field.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { APP_VERSION, APP_VERSION_FULL, getEnvironment, ENV_COLOR } from '../../lib/env.js'

/*
 * Debug console (super-admin only) — porté de l'ancien frontend. Trois onglets :
 * informations système/BD + version, logs du daemon, et validation Peppol d'une
 * facture. Accessible depuis le menu (sous Administration).
 */
export default function DebugPage() {
  const { t } = useTranslation()
  const { isSuperAdmin } = useCompany()
  const [active, setActive] = useState('info')
  if (!isSuperAdmin()) return <Navigate to="/app/dashboard" replace />

  const tabs = [
    { key: 'info', icon: 'info', label: t('debug.tabs.info'), el: <InfoTab /> },
    { key: 'logs', icon: 'settings', label: t('debug.tabs.logs'), el: <DaemonLogsTab /> },
    { key: 'peppol', icon: 'invoice', label: t('debug.tabs.peppol'), el: <PeppolTab /> },
  ]
  const current = tabs.find((tb) => tb.key === active)

  return (
    <Page title={t('debug.title')}>
      <Tabs tabs={tabs} active={active} onChange={setActive} />
      {current?.el}
    </Page>
  )
}

const fmtTs = (ts) => (ts ? `${new Date(ts).toLocaleDateString('fr-BE')} ${new Date(ts).toLocaleTimeString('fr-BE')}` : '—')

function Row({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--color-border-row)' }}>
      <span style={{ fontSize: 13, color: 'var(--color-text-muted)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13.5, color: 'var(--color-ink)', wordBreak: 'break-word' }}>{children}</span>
    </div>
  )
}

function InfoTab() {
  const { t } = useTranslation()
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => { api.get('/debug/info').then(({ data }) => setInfo(data)).finally(() => setLoading(false)) }, [])
  const env = getEnvironment()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Card>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, marginBottom: 12 }}>{t('debug.version.title')}</h3>
        <Row label={t('debug.version.app')}><code style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--color-primary)' }}>{APP_VERSION === 'dev' ? 'dev' : `v${APP_VERSION}`}</code>{APP_VERSION_FULL && APP_VERSION_FULL !== 'dev' && <span style={{ color: 'var(--color-text-muted)', marginLeft: 8, fontSize: 12 }}>{APP_VERSION_FULL}</span>}</Row>
        <Row label={t('debug.version.env')}><Badge tone={env === 'production' ? 'success' : 'warning'} style={{ background: env !== 'production' ? undefined : undefined }}>{env}</Badge></Row>
      </Card>

      <Card>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, marginBottom: 12 }}>{t('debug.db.title')}</h3>
        {loading ? <span style={{ color: 'var(--color-text-muted)' }}>{t('common.loading')}</span> : info?.database ? (
          <>
            <Row label={t('debug.db.version')}><strong>{info.database.version}</strong></Row>
            <Row label={t('debug.db.description')}>{info.database.description || '—'}</Row>
            <Row label={t('debug.db.appliedAt')}>{fmtTs(info.database.applied_at)}</Row>
          </>
        ) : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
      </Card>

      <Card>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, marginBottom: 12 }}>{t('debug.system.title')}</h3>
        {loading ? <span style={{ color: 'var(--color-text-muted)' }}>{t('common.loading')}</span> : info?.system ? (
          <>
            <Row label={t('debug.system.python')}>{info.system.python_version}</Row>
            <Row label={t('debug.system.serverTime')}>{fmtTs(info.system.timestamp)}</Row>
          </>
        ) : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
      </Card>
    </div>
  )
}

const LOG_TONE = { ERROR: 'danger', CRITICAL: 'danger', WARNING: 'warning', INFO: 'info', DEBUG: 'neutral' }
const PAGE_SIZE = 100

function DaemonLogsTab() {
  const { t } = useTranslation()
  const [logs, setLogs] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [level, setLevel] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE }
    if (level) params.level = level
    api.get('/debug/daemon-logs', { params })
      .then(({ data }) => { setLogs(data?.logs || []); setTotal(data?.total || 0) })
      .finally(() => setLoading(false))
  }, [page, level])
  useEffect(() => { load() }, [load])

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  return (
    <Card padding={0}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--color-border-row)', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Select value={level} onChange={(e) => { setLevel(e.target.value); setPage(0) }} style={{ width: 'auto', height: 38 }}>
            <option value="">{t('debug.logs.allLevels')}</option>
            {['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL'].map((l) => <option key={l} value={l}>{l}</option>)}
          </Select>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('debug.logs.total', { count: total })}</span>
        </div>
        <Button variant="secondary" icon="settings" onClick={load}>{t('archives.refresh')}</Button>
      </div>
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {loading ? <div style={{ padding: 24, color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
          : logs.length === 0 ? <div style={{ padding: 28, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('debug.logs.empty')}</div>
          : logs.map((l) => (
            <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '150px 80px 1fr', gap: 12, padding: '8px 18px', borderBottom: '1px solid var(--color-border-row)', alignItems: 'start' }}>
              <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{fmtTs(l.timestamp)}</span>
              <Badge tone={LOG_TONE[l.level] || 'neutral'}>{l.level}</Badge>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: 'var(--color-ink)', wordBreak: 'break-word' }}>{l.message}</div>
                <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{l.logger_name}</div>
                {l.exc_info && <pre style={{ margin: '4px 0 0', padding: 8, background: 'var(--color-ink)', color: '#e6b3b3', borderRadius: 8, fontSize: 11.5, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{l.exc_info}</pre>}
              </div>
            </div>
          ))}
      </div>
      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '12px 18px', borderTop: '1px solid var(--color-border-row)' }}>
          <Button variant="secondary" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>{t('common.previous')}</Button>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{page + 1} / {pages}</span>
          <Button variant="secondary" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>{t('common.next')}</Button>
        </div>
      )}
    </Card>
  )
}

function PeppolTab() {
  const { t } = useTranslation()
  const [companies, setCompanies] = useState([])
  const [companyId, setCompanyId] = useState('')
  const [invoices, setInvoices] = useState([])
  const [invoiceId, setInvoiceId] = useState('')
  const [report, setReport] = useState(null)
  const [logs, setLogs] = useState([])
  const [validating, setValidating] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => { api.get('/admin/peppol/companies').then(({ data }) => setCompanies(data?.companies || [])).catch(() => {}) }, [])
  useEffect(() => {
    setInvoices([]); setInvoiceId(''); setReport(null); setLogs([]); setError(null)
    if (!companyId) return
    api.get(`/admin/peppol/invoices/${companyId}`).then(({ data }) => setInvoices(data?.invoices || [])).catch(() => {})
  }, [companyId])

  const validate = async () => {
    if (!invoiceId) return
    setValidating(true); setReport(null); setLogs([]); setError(null)
    try {
      const { data } = await api.post(`/admin/peppol/validate/${invoiceId}`)
      setReport(data?.validation_report || null); setLogs(data?.logs || [])
    } catch (err) {
      setError(err?.response?.data?.error || t('debug.peppol.error'))
      if (err?.response?.data?.logs) setLogs(err.response.data.logs)
    } finally { setValidating(false) }
  }

  return (
    <Card>
      <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{t('debug.peppol.title')}</h3>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16 }}>{t('debug.peppol.intro')}</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('debug.peppol.company')}</span>
          <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">{t('debug.peppol.selectCompany')}</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.vat_number}</option>)}
          </Select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{t('debug.peppol.invoice')}</span>
          <Select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} disabled={!companyId}>
            <option value="">{companyId ? (invoices.length ? t('debug.peppol.selectInvoice') : t('debug.peppol.noInvoice')) : t('debug.peppol.selectCompanyFirst')}</option>
            {invoices.map((i) => <option key={i.id} value={i.id}>{i.invoice_number || `#${i.id}`}{i.customer_name ? ` — ${i.customer_name}` : ''}</option>)}
          </Select>
        </label>
        <Button variant="primary" icon="check" loading={validating} disabled={!invoiceId} onClick={validate}>{t('debug.peppol.validate')}</Button>
      </div>

      {error && <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 10, fontSize: 13 }}>{error}</div>}

      {report && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <Badge tone={report.is_valid ? 'success' : 'danger'}>{report.is_valid ? t('debug.peppol.valid') : t('debug.peppol.invalid')}</Badge>
            {typeof report.error_count === 'number' && <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('debug.peppol.errors', { count: report.error_count })}</span>}
          </div>
          <pre style={{ margin: 0, padding: 14, background: 'var(--color-ink)', color: '#e6e1f2', borderRadius: 10, fontSize: 12.5, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '40vh' }}>{JSON.stringify(report, null, 2)}</pre>
        </div>
      )}
      {logs.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>{t('debug.peppol.logs')}</div>
          <pre style={{ margin: 0, padding: 14, background: 'var(--color-bg-lilac)', borderRadius: 10, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', maxHeight: '30vh' }}>{logs.map((l) => (typeof l === 'string' ? l : `[${l.level || ''}] ${l.message || JSON.stringify(l)}`)).join('\n')}</pre>
        </div>
      )}
    </Card>
  )
}
